/**
 * Error Display Component
 * User-friendly error message display
 */

import type { SecureError } from '@/services/security/errorHandling';

export interface ErrorDisplayProps {
  /** Error object */
  error: SecureError | Error | string;

  /** Retry callback */
  onRetry?: () => void;

  /** Dismiss callback */
  onDismiss?: () => void;
}

export function ErrorDisplay({ error, onRetry, onDismiss }: ErrorDisplayProps) {
  // Extract error message
  const errorMessage = typeof error === 'string'
    ? error
    : 'message' in error
    ? error.message
    : 'An unexpected error occurred';

  const errorCode = typeof error === 'object' && 'code' in error ? error.code : undefined;

  return (
    <div
      className="ww-rounded-md ww-bg-red-50 ww-border ww-border-red-200 ww-p-4"
      role="alert"
      aria-live="assertive"
    >
      <div className="ww-flex">
        <div className="ww-flex-shrink-0">
          <svg
            className="ww-h-5 ww-w-5 ww-text-red-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ww-ml-3 ww-flex-1">
          <h3 className="ww-text-sm ww-font-medium ww-text-red-800">
            Error
            {errorCode && ` (${errorCode})`}
          </h3>
          <p className="ww-mt-1 ww-text-sm ww-text-red-700">{errorMessage}</p>

          {(onRetry || onDismiss) && (
            <div className="ww-mt-4 ww-flex ww-gap-3">
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="ww-text-sm ww-font-medium ww-text-red-800 hover:ww-text-red-900 focus:ww-outline-none focus:ww-underline"
                >
                  Try again
                </button>
              )}
              {onDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  className="ww-text-sm ww-font-medium ww-text-red-800 hover:ww-text-red-900 focus:ww-outline-none focus:ww-underline"
                >
                  Dismiss
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
